"use client";

import React from "react";
import { Facebook, Twitter, Instagram, ArrowUp, MapPin, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <ul className="space-y-3">
        {[1, 2, 3, 4, 5].map((_, i) => <li key={i} data-index={i}>
            <div className="h-5 w-24 bg-primary/10 animate-pulse rounded" />
          </li>)}
      </ul>;
  }
  if (!menuItems || menuItems.length === 0) {
    return null;
  }
  return <ul className="space-y-3">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} newTab={item.href?.startsWith("http")} className="text-muted-foreground hover:text-secondary transition-colors duration-200 text-sm font-medium font-sans">
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
export default function Footer() {
  const currentYear = new Date().getFullYear();
  const services = [{
    name: "Advisory & Management Support",
    path: "/ServicesPage"
  }, {
    name: "Financial Management & Reporting",
    path: "/ServicesPage"
  }, {
    name: "Systems, Projects & Capacity Building",
    path: "/ServicesPage"
  }];
  const socials = [{
    icon: Facebook,
    href: "https://facebook.com/riverbendaccounting",
    label: "Facebook"
  }, {
    icon: Twitter,
    href: "https://twitter.com/riverbendaccounting",
    label: "Twitter"
  }, {
    icon: Instagram,
    href: "https://instagram.com/riverbendaccounting",
    label: "Instagram"
  }];
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };
  return <section data-section-id="204602" id="footer" className="bg-muted text-foreground pt-20 pb-12 border-t border-border">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          <div className="flex flex-col items-start">
            <Link to="/" className="mb-6 inline-block">
              <img src="https://stable-woodcock.10web.cloud/wp-content/uploads/2026/04/image_q6l7uk6d.png" alt="RiverBend Accounting & Management Services" className="h-25 w-auto object-contain" />
            </Link>

            <p className="text-muted-foreground text-sm mb-6 max-w-xs leading-relaxed font-sans">
              Practical advisory, financial management, reporting, and project
              support for First Nation communities, Northern organizations,
              businesses, and partnerships.
            </p>

            <div className="flex items-center gap-3">
              {socials.map((social, i) => {
              const Icon = social.icon;
              return <Link key={social.label} to={social.href} newTab data-index={i} className="bg-accent hover:bg-accent/80 text-primary p-2 rounded-sm transition-colors duration-200" aria-label={social.label}>
                    <Icon className="size-4" />
                  </Link>;
            })}
            </div>
          </div>

          <div>
            <h3 className="font-default text-lg font-semibold mb-6 tracking-tight text-primary">
              Quick Links
            </h3>

            <MenuProvider menu_id="106">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          <div>
            <h3 className="font-default text-lg font-semibold mb-6 tracking-tight text-primary">
              Our Services
            </h3>

            <ul className="space-y-3">
              {services.map((service, i) => <li key={service.name} data-index={i}>
                  <Link to={service.path} className="text-muted-foreground hover:text-secondary transition-colors duration-200 text-sm font-medium font-sans">
                    {service.name}
                  </Link>
                </li>)}
            </ul>
          </div>

          <div>
            <h3 className="font-default text-lg font-semibold mb-6 tracking-tight text-primary">
              Location & Hours
            </h3>

            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-muted-foreground text-sm font-sans">
                <MapPin className="size-5 shrink-0 text-secondary mt-0.5" />
                <span>
                  400-1186 Roland St.
                  <br />
                  Thunder Bay, Ontario
                  <br />
                  P7B 5M4
                </span>
              </li>

              <li className="flex items-start gap-3 text-muted-foreground text-sm font-sans">
                <Clock className="size-5 shrink-0 text-secondary mt-0.5" />
                <span>Mon-Fri: 8:00 AM - 5:00 PM</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-border flex flex-col md:flex-row items-center justify-between gap-6">
          <p className="text-muted-foreground/60 text-sm text-center md:text-left font-sans">
            &copy; {currentYear} RiverBend Accounting & Management Services
            Professional Corp. All rights reserved.
          </p>

          <Button variant="ghost" size="sm" onClick={scrollToTop} className="text-muted-foreground hover:text-primary hover:bg-accent gap-2 rounded-sm px-4 font-sans">
            Back to Top
            <ArrowUp className="size-4" />
          </Button>
        </div>
      </div>
    </section>;
}